import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TrialState } from '../../../api/model/organisationSummaryDto';

/**
 * DOCU-1043: Zustand der Testphase einer Organisation, als Abzeichen.
 *
 * Nach dem Vorbild von <c>app-subscription-badge</c>. Der Zustand wird **nicht** hier
 * berechnet — er kommt fertig aus der API (TrialStatus.Derive, DOCU-1048), damit die
 * Definition von „abgelaufen und wird noch benutzt" nur an einer Stelle existiert.
 *
 * `expiredAndActive` ist bewusst ein eigener Eingang und kein sechster Zustand: Fachlich
 * ist es dieselbe abgelaufene Testphase, nur eine mit Gesprächsanlass. Wer den Filter auf
 * „abgelaufen" stellt, soll beide sehen.
 */
@Component({
  selector: 'app-trial-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span *ngIf="state && state !== 'none'"
          class="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
          [ngClass]="style"
          [title]="tooltip">
      {{ label | translate }}<span *ngIf="showDays">&nbsp;· {{ daysLeft }}&nbsp;T</span>
    </span>
    <span *ngIf="!state || state === 'none'" class="text-secondary">–</span>
  `,
})
export class TrialBadgeComponent {
  @Input() state: TrialState | undefined;
  @Input() daysLeft: number | null | undefined;
  @Input() expiredAndActive = false;
  @Input() endsAt: string | null | undefined;

  /** Resttage nur anzeigen, solange sie in die Zukunft zeigen. */
  get showDays(): boolean {
    return (this.state === 'running' || this.state === 'endingSoon')
      && this.daysLeft != null && this.daysLeft > 0;
  }

  get label(): string {
    if (this.state === 'expired' && this.expiredAndActive) return 'TRIAL.STATE_EXPIRED_ACTIVE';
    switch (this.state) {
      case 'running':    return 'TRIAL.STATE_RUNNING';
      case 'endingSoon': return 'TRIAL.STATE_ENDING_SOON';
      case 'expired':    return 'TRIAL.STATE_EXPIRED';
      case 'converted':  return 'TRIAL.STATE_CONVERTED';
      default:           return '';
    }
  }

  get tooltip(): string {
    return this.endsAt ? new Date(this.endsAt).toLocaleDateString('de-DE') : '';
  }

  get style(): string {
    if (this.state === 'expired' && this.expiredAndActive) {
      // Bewusst hervorgehoben: Das ist die einzige Zeile, bei der jemand handeln soll.
      return 'bg-amber-100 text-amber-900 ring-1 ring-amber-300';
    }
    switch (this.state) {
      case 'running':    return 'bg-blue-100 text-blue-800';
      case 'endingSoon': return 'bg-orange-100 text-orange-800';
      case 'expired':    return 'bg-gray-200 text-gray-700';
      case 'converted':  return 'bg-green-100 text-green-800';
      default:           return '';
    }
  }
}
